import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useGQL_team, usePlacesForTeam } from '@/contexts/graphql-and-swr-apis'
import { TeamCardSettingsTab } from '@/components/team/TeamCardSettingsTab'
import { TeamScreensTable } from '@/components/team/TeamScreensTable'
import { PageContentWrapper, SimpleCard, SimpleButton } from '@unwrittenmedia/simple-components'
import SimplePageHeader2 from '@/components/SimplePageHeader2'
import CardDetailItem from '@/components/cards/CardDetailItem'
import { formatBillingDate } from '@/helpers/subscriptionInfo'
import { createAuthGuard } from '@/lib/authGuard'
import { SMDTeamEditor } from '@/components/modals/SMDTeamEditor'
import SimpleModalDialog from '@/components/SimpleModalDialog'
import { useState, useRef } from 'react'
import { faCheckCircle, faChurch } from '@fortawesome/pro-solid-svg-icons'

export const Route = createFileRoute('/team/$teamId/manage')({
    beforeLoad: createAuthGuard(),
    component: TeamManagePage,
})


function TMPTeamInfoSection({ team, sub }: { team: any, sub: any }) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const bodyComponentRef = useRef(null);
    return (
        <section id="info">
            <SimpleCard title="Team Info" action={
                <SimpleButton
                    color="primary"
                    size="sm"
                    onClick={() => setIsModalOpen(true)}
                >
                    Edit
                </SimpleButton>
            }>
                <dl className="space-y-4">
                    <CardDetailItem title="Name">{team.name}</CardDetailItem>
                    {team.handle && (
                        <CardDetailItem title="Handle"><a target="_blank" href={`https://sermon-slides.com/t/${team.handle}`}>@{team.handle}</a></CardDetailItem>
                    )}
                    {team.description && (
                        <CardDetailItem title="Description">{team.description}</CardDetailItem>
                    )}
                    <CardDetailItem title="Team ID">{team.id}</CardDetailItem>
                    <CardDetailItem title="Screens">{String((team.places || []).length)} of {sub?.screenCount}</CardDetailItem>
                    {team.deleted && (
                        <CardDetailItem title="Status">Deleted</CardDetailItem>
                    )}
                </dl>
            </SimpleCard>

            <SimpleModalDialog
                open={isModalOpen}
                onClose={(buttonId) => {
                    console.log('Dialog closed with button:', buttonId)
                    setIsModalOpen(false)
                }}
                dialogTitle="Edit Team"
                icon={faChurch}
                iconColor="blue"
                BodyComponent={SMDTeamEditor}
                data={{teamId: team.id}}
                bodyComponentRef={bodyComponentRef}
                buttons={[
                    {
                        id: 'cancel',
                        label: 'Cancel',
                        color: 'secondary',
                        // faIcon: faClock,
                    },
                    {
                        id: 'save',
                        label: 'Save',
                        color: 'primary',
                        faIcon: faCheckCircle,
                    },
                ]}
            />


        </section>
    )
}

/**
 * Section of the page with the screens card.
 * 
 * @param param0 The props object containing teamId and places.
 * @returns A JSX element representing the screens section for the team.
 */
function TMPScreensSection({ teamId, places }: { teamId: string, places: any[] }) {
    const navigate = useNavigate()
    return (
        <SimpleCard title="Screens" action={
            <SimpleButton
                color="primary"
                size="sm"
                onClick={() => navigate({ to: `/places/new?team=${teamId}` })}
            >
                Add Screen
            </SimpleButton>
        }>
            <TeamScreensTable teamId={teamId} places={places} />
        </SimpleCard>
    )
}


function TeamManagePage() {
    const navigate = useNavigate()
    const { teamId } = Route.useParams()
    const { data, isLoading, error } = useGQL_team(teamId)
    const { places } = usePlacesForTeam(teamId, { withPlaceStatus: true })

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-gray-900 dark:text-white">Loading team...</div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-red-600 dark:text-red-400">Error loading team: {error.message}</div>
            </div>
        )
    }

    const team = data?.team
    const sub = team?.subscriptionInfo

    if (!team) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-gray-500 dark:text-mist-500">Team not found.</div>
            </div>
        )
    }

    return (
        <PageContentWrapper>
            <SimplePageHeader2
                title={team.name}
                titleBreadcrumbs={[{ label: 'Teams', to: '/today' }]}
                description="Manage team settings, members, and billing."
                actionButtonText="Back"
                actionButtonColor="secondary"
                actionButtonOnClick={() => navigate({ to: '/today' })}
            />

            <div className="space-y-6">
                {/* Team Info */}
                <TMPTeamInfoSection team={team} sub={sub} />

                <section id="screens">
                    <TMPScreensSection teamId={team.id} places={places} />
                </section>

                <section id="subscription">
                    <SimpleCard
                        title="Subscription"
                        action={
                            <SimpleButton
                                color="primary"
                                size="sm"
                                onClick={() => navigate({
                                    to: '/team/add-team',
                                    search: {
                                        teamId: team.id,
                                        ...(sub?.subPlanId ? { planId: sub.subPlanId } : {}),
                                        ...(team.places?.length ? { screenCount: team.places.length } : {}),
                                    },
                                })}
                            >
                                Upgrade
                            </SimpleButton>
                        }
                    >
                        {sub ? (
                            <dl className="space-y-4">
                                {sub.subPlanName && (
                                    <CardDetailItem title="Plan">{sub.subPlanName}</CardDetailItem>
                                )}
                                {sub.subPlanId && (
                                    <CardDetailItem title="Plan ID">{sub.subPlanId}</CardDetailItem>
                                )}
                                {sub.subPlanFrequency && (
                                    <CardDetailItem title="Billing Frequency">
                                        {sub.subPlanFrequency.charAt(0).toUpperCase() + sub.subPlanFrequency.slice(1)}
                                    </CardDetailItem>
                                )}
                                {sub.nextBillingDate && (
                                    <CardDetailItem title="Next Billing Date">
                                        {formatBillingDate(sub.nextBillingDate) ?? sub.nextBillingDate}
                                    </CardDetailItem>
                                )}
                                <CardDetailItem title="Status">
                                    {sub.isTrial ? 'Trial' : 'Active'}
                                </CardDetailItem>
                            </dl>
                        ) : (
                            <p className="text-sm text-gray-500 dark:text-mist-500">No subscription info available.</p>
                        )}
                    </SimpleCard>
                </section>

                {/* Members & Billing — rendered via TeamCardSettingsTab */}
                <section id="users">
                    <div className="bg-white dark:bg-mist-900 shadow rounded-lg ring-1 ring-gray-200 dark:ring-mist-800 overflow-hidden">
                        <TeamCardSettingsTab team={team} />
                    </div>
                </section>
            </div>
        </PageContentWrapper>
    )
}
